import BreadcrumbDates from "../../../../../../../components/core/blocks/BreadcrumbDates";
import { AmcInstanceDetails } from "../../../../../../../modules/amc/components/instance/AmcInstanceDetails";
import { Col, Row } from "react-bootstrap";
import {
  AMC_QUERY_EXPLORER_BREADCRUMB_SPEC,
  CANNED_QUERIES,
  FEATURED_REPORT_TYPES_WITH_DATE_SUPPORT,
  FEATURED_REPORTS,
  normalizeQueryName,
} from "../../../../../../../modules/amc/utils/utils";
import { DASHBOARD_LINKS } from "../../../../../../../utils/dashboardLinks";
import { useMemo } from "react";
import { Navigate, createFileRoute } from "@tanstack/react-router";
import moment from "moment";
import Loading from "../../../../../../../components/core/blocks/Loading";
import { useAMCReportData } from "../../../../../../../modules/amc/hooks/useAMCReportData";
import { useAMCQueryData } from "../../../../../../../modules/amc/hooks/useAMCQueryData";
import { useModuleEnabled } from "../../../../../../../redux/selectors/modules";
import AMCReportTable from "../../../../../../../modules/amc/components/AMCReportTable";
import AMCGeoReport from "../../../../../../../modules/amc/components/AMCGeoReport";
import AMCFirstTouchAttrReport from "../../../../../../../modules/amc/components/AMCFirstTouchAttrReport";
import AMCSearchTermPlacementReport from "../../../../../../../modules/amc/components/AMCSearchTermPlacementReport";
import PathToPurchaseReport from "../../../../../../../modules/amc/components/pathToPurchase/PathToPurchaseReport";
import { formatSubscriptionName } from "../../../../../../../views/amc/AMCReportsListing";
import AMCReachImpressionFrequency from "../../../../../../../modules/amc/components/AMCReachImpressionFrequency";
import AMCTimeToConversion from "../../../../../../../modules/amc/components/AMCTimeToConversion";
import { useTranslation } from "react-i18next";
import { useDateFormatter } from "../../../../../../../i18n/useDateFormatter";

const QueryTypeReport = ({ reportData, reportDetails, queryDetails, isLoading }) => {
  switch (queryDetails?.query_type) {
    case "DSP_CAMPAIGN_PERFORMANCE_BY_LOCATION":
      return <AMCGeoReport reportData={reportData} reportDetails={reportDetails} queryDetails={queryDetails} />;
    case "CAMPAIGN_PERFORANCE_FIRST_TOUCH_ATTRIBUTION":
      return (
        <AMCFirstTouchAttrReport reportData={reportData} reportDetails={reportDetails} queryDetails={queryDetails} />
      );
    case "CAMPAIGN_STR_PLACEMENT_PERFORMANCE":
      return (
        <AMCSearchTermPlacementReport
          reportData={reportData}
          reportDetails={reportDetails}
          queryDetails={queryDetails}
        />
      );
    case "PATH_TO_PURCHASE_GROUPS_INTERNAL":
      return (
        <PathToPurchaseReport
          reportData={reportData}
          reportDetails={reportDetails}
          queryDetails={queryDetails}
          isLoading={isLoading}
        />
      );
    case "REACH_IMPRESSION_FREQUENCY":
      return (
        <AMCReachImpressionFrequency
          reportData={reportData}
          reportDetails={reportDetails}
          queryDetails={queryDetails}
          isLoading={isLoading}
        />
      );
    case "TIME_TO_CONVERSION":
      return (
        <AMCTimeToConversion
          reportData={reportData}
          reportDetails={reportDetails}
          queryDetails={queryDetails}
          isLoading={isLoading}
        />
      );
    default:
      return <AMCReportTable reportData={reportData} reportDetails={reportDetails} queryDetails={queryDetails} />;
  }
};

const AMCReport = () => {
  const { id: reportId } = Route.useParams();
  const { t } = useTranslation(["amc"]);
  const fmt = useDateFormatter("long");

  const hasAMCModule = useModuleEnabled("AMC");

  const { data: queryData, isLoading } = useAMCQueryData();

  const {
    reportMetadata: reportDetails,
    isLoading: isReportDetailsLoading,
    data: reportData,
  } = useAMCReportData(reportId);

  const { reportMetadata: queryReportsData } = useAMCReportData(null, reportDetails?.query_type, false, null, true, {});

  const queryDetails = useMemo(() => {
    if (FEATURED_REPORT_TYPES_WITH_DATE_SUPPORT.includes(reportDetails?.query_type)) {
      return { ...FEATURED_REPORTS.find((option) => option.name === reportDetails?.query_type), ...reportDetails };
    }

    const querySubscription =
      queryData?.find((subscribedQuery) => {
        return subscribedQuery?.query_type === reportDetails?.query_type;
      }) ?? {};
    const reportQueryDetails = CANNED_QUERIES.find(
      (query) => query.name === normalizeQueryName(querySubscription?.query_type),
    );

    return { ...reportQueryDetails, ...querySubscription };
  }, [queryData, reportDetails]);

  const queryOptions = useMemo(() => {
    const subscribedReportOptions =
      queryData?.map((query) => {
        const displayName = formatSubscriptionName(query, t);
        return { label: displayName, value: query.id };
      }) ?? [];

    const featuredReportOptions =
      FEATURED_REPORTS?.filter((reportType) => FEATURED_REPORT_TYPES_WITH_DATE_SUPPORT.includes(reportType.name)).map(
        (reportType) => ({
          label: typeof reportType.display_name === "function" ? t(reportType.display_name) : reportType.display_name,
          value: reportType.href,
          featured: true,
        }),
      ) ?? [];

    return [...subscribedReportOptions, ...featuredReportOptions];
  }, [queryData, t]);

  const queryDetailsDisplayName =
    typeof queryDetails?.display_name === "function" ? t(queryDetails.display_name) : queryDetails?.display_name;

  const breadCrumbItems = useMemo(() => {
    const breadCrumbSpec = [
      {
        name: t(($) => $.amc["AMC Dashboard"]),
        href: "/user/amc/dashboard",
        unsorted: true,
        dropdownLinkOptions: DASHBOARD_LINKS,
        dropdownSubtitle: t(($) => $.amc["Reports"]),
        activeOption: t(($) => $.amc["AMC Dashboard"]),
      },
      {
        name: queryDetailsDisplayName,
        href: queryDetails?.featured ? queryDetails?.href : `/user/amc/dashboard/reports/${queryDetails?.id}`,
        dropdownLinkOptions: queryOptions
          ?.map((query) => {
            const { label, value, featured } = query;
            return {
              name: label,
              href: featured ? value : `/user/amc/dashboard/reports/${value}`,
            };
          })
          .filter((q) => !!q.name),
        searchable: true,
        searchPlaceholderText: t(($) => $.amc["Search subscribed queries..."]),
        dropdownSubtitle: t(($) => $.amc["Subscribed Queries"]),
        activeOption: queryDetailsDisplayName,
      },
      {
        name:
          reportDetails?.metadata?.timeWindowStart ?
            `${fmt.format(moment(reportDetails?.metadata?.timeWindowStart).toDate())} - ${fmt.format(moment(reportDetails?.metadata?.timeWindowEnd).toDate())}`
          : "",
        unsorted: true,
        href: `/user/amc/dashboard/reports/details/${reportDetails?.id}`,
        dropdownLinkOptions: queryReportsData?.map((report) => {
          const {
            id,
            metadata: { timeWindowStart: startDate, timeWindowEnd: endDate },
          } = report;

          return {
            name: `${fmt.format(moment(startDate).toDate())} - ${fmt.format(moment(endDate).toDate())}`,
            href: `/user/amc/dashboard/reports/details/${id}`,
          };
        }),
        searchable: true,
        searchPlaceholderText: t(($) => $.amc["Search query reports..."]),
        dropdownSubtitle: t(($) => $.amc["Query Reports"]),
      },
    ];

    if (!breadCrumbSpec[0].dropdownLinkOptions?.find((o) => o.name === AMC_QUERY_EXPLORER_BREADCRUMB_SPEC.name)) {
      breadCrumbSpec[0].dropdownLinkOptions.push(AMC_QUERY_EXPLORER_BREADCRUMB_SPEC);
    }
    return breadCrumbSpec;
  }, [reportDetails, queryDetails, queryDetailsDisplayName, queryOptions, queryReportsData, t, fmt]);

  if (!hasAMCModule) {
    return <Navigate to="/user/dashboard" replace />;
  }

  return (
    <div style={{ minHeight: "calc(100vh - 150px" }}>
      <Row>
        <Col xs={12} className="mb-4">
          <BreadcrumbDates items={breadCrumbItems} hideDateSelector={true}>
            <AmcInstanceDetails />
          </BreadcrumbDates>
        </Col>
      </Row>
      {isReportDetailsLoading || isLoading ?
        <Loading fullPage={true} />
      : <>
          <Row style={{ padding: "20px 150px" }}>
            <Col xs={12} className="text-center">
              <h1 className="fw-bold fs-2 mb-2">
                {t(($) => $.amc["{displayName} Report"], { displayName: queryDetailsDisplayName })}
              </h1>
              <p style={{ fontSize: "14px" }}>
                {t(($) => $.amc["{start} to {end}"], {
                  start: fmt.format(moment(reportDetails?.metadata?.timeWindowStart).toDate()),
                  end: fmt.format(moment(reportDetails?.metadata?.timeWindowEnd).toDate()),
                })}
              </p>
            </Col>
          </Row>
          <Row className="px-5 w-100 pb-5">
            <Col xs={12} className="px-5">
              <QueryTypeReport
                reportData={reportData}
                reportDetails={reportDetails}
                queryDetails={queryDetails}
                isLoading={isReportDetailsLoading}
              />
            </Col>
          </Row>
        </>
      }
    </div>
  );
};

export const Route = createFileRoute("/_authenticated/user/amc/dashboard/reports/details/$id")({
  component: AMCReport,
  staticData: { application: "Advertising" },
});
